import { composeProgress } from "apollo/utils";
import {
  isRehabFormType,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  RECARE_FILE_TYPE,
  REHAB_FORM_FILE_TYPES,
  REHAB_FORM_NAMES,
  SEARCH_ACTION_DELETE_FORM,
  SEARCH_ACTION_EDIT_SEARCH,
  SEARCH_ACTION_GENERATE_FORM,
  TRACK_EVENTS,
} from "core/consts";
import { getSearchActions } from "core/model/auctions";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { convertStringToDate } from "core/model/utils/dates";
import { getErrorMessage } from "core/model/utils/errors";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { grammaticalList } from "core/model/utils/strings";
import { ICD_CODE_CHARACTER_LIMIT } from "core/schemas/shared";
import {
  AnyObject,
  Auction,
  AuctionForm,
  DRVGeneralFormData,
  DRVMedicalFormData,
  GetOntologyType,
  GKVGeneralFormData,
  GKVMedicalFormData,
  InfectionAndGerms,
  Patient,
  PredicamentState,
  RehabFormName,
  ValueOf,
} from "core/types";
import { TrackingEventName } from "core/validationSchemas/tracking";
import { isValid } from "date-fns";
import { useConfirmationDialog } from "ds_legacy/components/ConfirmationDialog/useConfirmationDialog";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useDeleteUnsharedFileByType } from "dsl/ecosystems/PatientFiles/useDeleteUnsharedFileByType";
import { FORM_NAME_TO_FILE_TYPE } from "dsl/ecosystems/RecareDocs/Modal/utils";
import { scrollToTop } from "dsl/hooks/useScrollTo";
import useSearchAction from "dsl/hooks/useSearchAction";
import { convertInfectionsToOntologiesIds } from "dsl/molecules/Messenger/Forms/Decline/NoCapacityReasons/values";
import cloneDeep from "lodash/cloneDeep";
import { CircleHelpIcon } from "lucide-react";
import { resolveArhVersion } from "onprem/resolveArhVersion";
import { PDFForm } from "pdf-lib";
import { CSSProperties, useCallback } from "react";
import {
  convertEncryptedFieldIn,
  convertEncryptedFieldOut,
} from "react-forms-state";
import { FormContainerState } from "react-forms-state/src/FormContainer";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { formatDate } from "../utils";
import {
  IMPORT_AUCTION_ID,
  REIMPORT_AUCTION_ID,
  SAVE_AND_CREATE_PDF_ID,
  SAVE_AND_OVERWRITE_PDF_ID,
} from "./RehabFormWrapper";

type RehabFormTypeMapping = {
  [REHAB_FORM_NAMES.DRV_MEDICAL]: DRVMedicalFormData;
  [REHAB_FORM_NAMES.DRV_GENERAL]: DRVGeneralFormData;
  [REHAB_FORM_NAMES.GKV_MEDICAL]: GKVMedicalFormData;
  [REHAB_FORM_NAMES.GKV_GENERAL]: GKVGeneralFormData;
};

export type RehabFormDataMapped<T extends RehabFormName> =
  RehabFormTypeMapping[T];

const PDF_TRACK_EVENTS: Record<
  string,
  { error: TrackingEventName; success: TrackingEventName }
> = {
  [IMPORT_AUCTION_ID]: {
    error: TRACK_EVENTS.REHAB_FORM_IMPORT_ERROR,
    success: TRACK_EVENTS.REHAB_FORM_IMPORT_SUCCESS,
  },
  [REIMPORT_AUCTION_ID]: {
    error: TRACK_EVENTS.REHAB_FORM_REIMPORT_ERROR,
    success: TRACK_EVENTS.REHAB_FORM_REIMPORT_SUCCESS,
  },
  [SAVE_AND_CREATE_PDF_ID]: {
    error: TRACK_EVENTS.REHAB_FORM_SAVE_AND_CREATE_PDF_ERROR,
    success: TRACK_EVENTS.REHAB_FORM_SAVE_AND_CREATE_PDF_SUCCESS,
  },
  [SAVE_AND_OVERWRITE_PDF_ID]: {
    error: TRACK_EVENTS.REHAB_FORM_SAVE_AND_OVERWRITE_PDF_ERROR,
    success: TRACK_EVENTS.REHAB_FORM_SAVE_AND_OVERWRITE_PDF_SUCCESS,
  },
};

export const areNewRehabFormsActive = (auction: Auction): boolean => {
  const { [SEARCH_ACTION_GENERATE_FORM]: action } = getSearchActions(auction);
  if (!action || action.context?.disabled) {
    return false;
  }

  const availableFormTypes = action.context?.values ?? [];

  return REHAB_FORM_FILE_TYPES.some((type) =>
    availableFormTypes.includes(type),
  );
};

export function getMedicalFormApproved(
  auctionForms: AuctionForm[] | undefined,
): boolean | undefined {
  const form = auctionForms?.find(
    (f) =>
      f.form_type === RECARE_FILE_TYPE.GKV_MEDICAL_FORM ||
      f.form_type === RECARE_FILE_TYPE.DRV_MEDICAL_FORM,
  );
  return form?.metadata?.is_doctor_approved;
}

export function useGetFormSubmit({
  auction,
  auctionForms,
  formName,
  isMedicalFormApproved,
}: {
  auction: Auction | undefined;
  formName: RehabFormName;
  auctionForms: AuctionForm[] | undefined;
  isMedicalFormApproved?: boolean;
}) {
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const [confirmProps, confirm] = useConfirmationDialog();
  const [generateForm, generateFormProgress, resetProgress] = useSearchAction({
    actionType: SEARCH_ACTION_GENERATE_FORM,
  });
  const [editSearch, editSearchProgress] = useSearchAction({
    actionType: SEARCH_ACTION_EDIT_SEARCH,
  });
  const toast = useToast();
  const { isRedesignActive } = useIsDocsAndExtractActive();

  const onSubmit = useCallback(
    async (
      formData: RehabFormDataMapped<typeof formName>,
      formState: FormContainerState,
      clickEvent: React.MouseEvent<HTMLButtonElement>,
      buttonId?: keyof typeof PDF_TRACK_EVENTS,
    ) => {
      if (!formState.dirty || !formName || !auction) return;

      if (
        formName === REHAB_FORM_NAMES.GKV_MEDICAL &&
        isMedicalFormApproved &&
        !isRedesignActive
      ) {
        const confirmed = await confirm({
          cancelText:
            translations.patientForms.medicalForm.approvalConfirmation
              .buttonCancel,
          content:
            translations.patientForms.medicalForm.approvalConfirmation
              .confirmationModalText,
          headerIcon: (
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-light">
              <CircleHelpIcon
                aria-hidden
                className="h-8 w-8 text-primary-dark"
              />
            </span>
          ),
          id: "save-and-overwrite",
          submitText:
            translations.patientForms.medicalForm.approvalConfirmation
              .buttonSave,
          title:
            translations.patientForms.medicalForm.approvalConfirmation
              .confirmationModalTitle,
          useNewDesign: true,
        });

        if (!confirmed) {
          return;
        }
      }

      const fileType = FORM_NAME_TO_FILE_TYPE[formName];

      const isGkv =
        fileType === RECARE_FILE_TYPE.GKV_GENERAL_FORM ||
        fileType === RECARE_FILE_TYPE.GKV_MEDICAL_FORM;

      const caseId = (formData as GKVGeneralFormData).case_id;
      if (caseId && auction.patient.external_id !== caseId) {
        const auctionWithUpdatedPatient = cloneDeep(auction);
        auctionWithUpdatedPatient.patient.external_id = caseId;

        await editSearch({
          auction,
          context: { auction: auctionWithUpdatedPatient },
        });
      }

      const resolvedSchemaVersion = isGkv
        ? resolveArhVersion(auctionForms)
        : undefined;

      const updatedAuction = {
        ...auction,
        forms: [
          {
            form_data: convertEncryptedFieldOut(JSON.stringify(formData)),
            form_type: fileType,
            ...(resolvedSchemaVersion && {
              schema_version: resolvedSchemaVersion,
            }),
            ...(resolvedSchemaVersion && {
              metadata: { schema_version: resolvedSchemaVersion },
            }),
          },
        ],
      } as Auction;

      await generateForm({
        auction,
        context: {
          auction: updatedAuction,
          form_type: fileType,
        },
        onCompleted: (generated) => {
          const returnedForms = generated?.forms?.filter((form) =>
            isRehabFormType(form.form_type),
          );

          if (!returnedForms?.length) {
            throw new Error("no rehab form returned on submit");
          }

          if (!isRedesignActive) {
            const buttonTargetId = (clickEvent?.target as HTMLButtonElement)
              ?.id;
            const { success } = PDF_TRACK_EVENTS[buttonTargetId] || {};
            trackEvent({ name: success });
            toast({
              action: {
                label: translations.patientForms.transitionalCareForm.toTheTop,
                onClick: () => scrollToTop(),
              },
              color: "success",
              message:
                translations.patientForms.transitionalCareForm.pdfCreated,
            });
          } else if (buttonId) {
            const { success } = PDF_TRACK_EVENTS[buttonId] || {};
            trackEvent({ name: success });
          }

          resetProgress();
        },
        onError: (err) => {
          const derivedButtonId =
            (clickEvent?.currentTarget as HTMLElement)?.id || buttonId;
          const { error } =
            PDF_TRACK_EVENTS[
              derivedButtonId as keyof typeof PDF_TRACK_EVENTS
            ] || {};
          if (error) trackEvent({ name: error });

          console.error(`error saving rehab forms - ${getErrorMessage(err)}`);
          toast({
            color: "danger",
            message: translations.auctionRequest.tryAgain,
          });
        },
      });
    },
    [
      convertEncryptedFieldOut,
      generateForm,
      auction,
      auctionForms,
      translations,
    ],
  );

  return {
    confirmProps,
    onSubmit,
    onSubmitProgress: composeProgress([
      editSearchProgress,
      generateFormProgress,
    ]),
  };
}

export function useDeleteConfirmation({
  auction,
  formName,
  formOnChange,
}: {
  auction: Auction;
  formName: RehabFormName;
  formOnChange?: (value: any, statePath: string, validation: any) => void;
}) {
  const toast = useToast();
  const translations = useTranslations();
  const [deleteForm, deleteFormProgress, resetProgress] = useSearchAction({
    actionType: SEARCH_ACTION_DELETE_FORM,
  });
  const [confirmProps, confirm] = useConfirmationDialog();

  const { deleteUnsharedFile } = useDeleteUnsharedFileByType({
    auctionId: auction.id,
    recareFileType: FORM_NAME_TO_FILE_TYPE[formName],
  });

  const onDelete = async (formValue?: RehabFormDataMapped<typeof formName>) => {
    try {
      const confirmed = await confirm({
        cancelButtonProps: {
          id: `cancel-delete-${formName}`,
        },
        cancelText: translations.patientForms.fileDeletion.cancelButton,
        confirmButtonProps: {
          color: "error",
          id: `confirm-delete-${formName}`,
          variant: "contained",
        },
        content: translations.patientForms.fileDeletion.text,
        submitText: translations.patientForms.fileDeletion.confirmButton,
        title: translations.patientForms.fileDeletion.title,
      });

      if (!confirmed) return null;

      await deleteForm({
        auction,
        context: {
          form_type: FORM_NAME_TO_FILE_TYPE[formName],
        },
      });

      await deleteUnsharedFile();
      toast({ color: "success", message: translations.general.success });
      if (typeof formOnChange === "function" && formValue) {
        Object.keys(formValue).forEach((key) => formOnChange(null, key, null));
      }
      resetProgress();
    } catch (err) {
      toast({ color: "danger", message: translations.general.error });
      console.error(
        `error deleting transitional care form: ${getErrorMessage(err)}`,
      );
    }
  };

  return { confirmProps, deleteFormProgress, onDelete };
}

export function getFormInputValue<T extends RehabFormName>({
  auctionForms,
  formName,
}: {
  formName: T;
  auctionForms: AuctionForm[] | undefined;
}): {
  form: AuctionForm | null;
  formData: RehabFormDataMapped<T> | null;
} {
  if (!auctionForms?.length) return { form: null, formData: null };

  const fileType = FORM_NAME_TO_FILE_TYPE[formName];
  const form = auctionForms.find((f) => f.form_type === fileType) ?? null;

  if (!form?.form_data) return { form: null, formData: null };

  const stringified = convertEncryptedFieldIn(form.form_data as AnyObject);

  const parsedFormData = JSON.parse(stringified || "{}") as RehabFormDataMapped<
    typeof formName
  >;

  return {
    form,
    formData: parsedFormData,
  };
}

function normalizeInsuranceIkDisplay(
  value: string | number | null | undefined,
): string | null {
  if (value == null) {
    return null;
  }
  const s = String(value).trim();
  return s === "" ? null : s;
}

/**
 * True when at least one GKV form has saved `form_data` and the normalized
 * form IK (general, then medical) is not the same value as
 * `patient.profile.financing.insurance.ik_number` (including when one is null).
 */
export function hasGkvInsuranceIkProfileMismatch({
  auctionForms,
  patient,
}: {
  auctionForms: AuctionForm[] | undefined;
  patient: Patient;
}): boolean {
  if (!auctionForms?.length) {
    return false;
  }

  const gkvGeneral = getFormInputValue({
    auctionForms,
    formName: REHAB_FORM_NAMES.GKV_GENERAL,
  });
  const gkvMedical = getFormInputValue({
    auctionForms,
    formName: REHAB_FORM_NAMES.GKV_MEDICAL,
  });

  if (gkvGeneral.formData == null && gkvMedical.formData == null) {
    return false;
  }

  const formIk =
    normalizeInsuranceIkDisplay(gkvGeneral.formData?.insurance_ik_number) ??
    normalizeInsuranceIkDisplay(gkvMedical.formData?.insurance_ik_number);

  const profileIk = normalizeInsuranceIkDisplay(
    patient.profile?.financing?.insurance?.ik_number,
  );

  return formIk !== profileIk;
}

export const booleanToPredicamentState = (
  boolValue: boolean | null | undefined,
): PredicamentState | null => {
  switch (boolValue) {
    case true:
      return PREDICAMENT_STATE_YES;
    case false:
      return PREDICAMENT_STATE_NO;
    default:
      return null;
  }
};

export const auctionBirthdatetoUnix = (auction: Auction) => {
  const decryptedDate = auction.patient?.profile?.birth_date?.decrypted;

  return stringDateToUnix({ date: decryptedDate });
};

export const getActivableStyles = (show: boolean): CSSProperties => {
  if (show) return {};
  return {
    display: "none",
  };
};

export function getInfection(
  auction: Auction,
  getOntology: GetOntologyType,
  translations: Translations,
) {
  const hasInfection =
    auction.patient.diagnosis?.infection_and_germs_state ===
    PREDICAMENT_STATE_YES;

  const infections = auction.patient.diagnosis?.infection_and_germs;

  if (!hasInfection || !infections) return null;

  const infectionKeys = Object.keys(infections) as Array<
    keyof InfectionAndGerms
  >;
  const trueInfections = infectionKeys.filter(
    (key) => infections[key] === true,
  );

  const infectionsIds = convertInfectionsToOntologiesIds(trueInfections);

  let infectionString = grammaticalList({
    array: infectionsIds,
    getOntology,
    ontologyType: ONTOLOGIES.INFECTION_AND_GERMS,
    translations,
  });

  if (infections.other) {
    infectionString = `${translations.general.other}${
      translations.general.colon
    } ${infections.other}${infectionString ? `, ${infectionString}` : ""}`;
  }

  return infectionString || null;
}

export const stringDateToUnix = ({
  date,
}: {
  date: string | null | undefined;
}) => {
  if (!date) {
    return null;
  }

  const parsed = convertStringToDate(date, "de");

  if (!isValid(parsed)) return null;

  // Interpret date-only strings as a calendar date at midnight UTC.
  // Otherwise tests and exports become timezone-dependent (e.g. CET vs UTC).
  return (
    Date.UTC(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate(),
      0,
      0,
      0,
      0,
    ) / 1000
  );
};

export const getICDCode = (
  auction: Auction,
  icdCodeIndex: 1 | 2 | 3 | 4 | 5,
) => {
  if (icdCodeIndex === 1 && auction?.patient?.diagnosis?.icd_code?.code) {
    return auction.patient.diagnosis.icd_code.code?.slice(
      0,
      ICD_CODE_CHARACTER_LIMIT,
    );
  }

  const codes = auction?.patient?.diagnosis?.additional_icd_codes ?? [];

  return (
    codes.at(icdCodeIndex - 1)?.code?.slice(0, ICD_CODE_CHARACTER_LIMIT) || null
  );
};

export const getPatientStreetHouseNumber = (auction: Auction) =>
  [
    auction.profile?.search_location?.street,
    auction.profile?.search_location?.encrypted_house_number?.decrypted,
  ]
    .truthy()
    .join(" ");

export const getHospitalName = (auction: Auction) =>
  auction.patient?.careseeker?.name;
export const getHospitalAddress = (auction: Auction) =>
  [
    auction.patient?.careseeker?.address?.street,
    auction.patient?.careseeker?.address?.street_number,
    auction.patient?.careseeker?.address?.zipcode,
    auction.patient?.careseeker?.address?.city,
  ]
    .truthy()
    .join(" ");

export const getPatientCity = (auction: Auction) => {
  const city = auction.profile?.search_location?.city;
  if (city?.includes(",")) {
    return city.split(",")[0].trim();
  }
  return city;
};

export const handlePdfDateField = <T extends RehabFormDataMapped<any>>({
  form,
  formFieldName,
  locale,
  removeSeparators,
  value,
}: {
  form: PDFForm;
  formFieldName: keyof T;
  locale: Locale;
  removeSeparators?: boolean;
  value?: ValueOf<T>;
}) => {
  if (!value) return;

  let date = formatDate(value as number, locale);
  if (removeSeparators) {
    date = date.replaceAll(".", "").replaceAll("/", "");
  }
  form.getTextField(formFieldName as string).setText(date);
};

export const handlePdfCheckboxes = <
  T extends Record<string, number>,
  K extends RehabFormDataMapped<any>,
>({
  form,
  formFieldName,
  possibleValues,
  value,
}: {
  form: PDFForm;
  formFieldName: keyof K;
  possibleValues: T;
  value: ValueOf<K>;
}) => {
  if (!value || !Array.isArray(value) || !value.length) {
    return;
  }
  Object.values(possibleValues).forEach((possibleValue) => {
    const checkbox = form.getCheckBox(
      `${String(formFieldName)}_${possibleValue}`,
    );
    if ((value as ValueOf<T>[]).includes(possibleValue as ValueOf<T>)) {
      checkbox.check();
    } else {
      checkbox.uncheck();
    }
  });
};
